<template>
  <teleport to="body">
    <transition name="a11y">
      <div v-if="ui.open" class="a11y-overlay" @click.self="ui.open = false">
        <aside class="a11y-panel" role="dialog" :aria-label="$t('a11y.title')">
          <header class="a11y-panel__head">
            <button class="a11y-x" :aria-label="$t('a11y.close')" @click="ui.open = false">&times;</button>
            <h2 class="a11y-panel__title">{{ $t('a11y.title') }}</h2>
          </header>

          <div class="a11y-panel__body">
            <!-- Vision modes -->
            <div class="a11y-modes">
              <button
                v-for="m in modes"
                :key="m.id"
                type="button"
                class="a11y-mode"
                :class="{ 'a11y-mode--active': settings.mode === m.id }"
                :title="$t(m.labelKey)"
                :aria-label="$t(m.labelKey)"
                @click="settings.mode = m.id"
                v-html="m.icon"
              ></button>
            </div>

            <!-- Font size -->
            <label class="a11y-row">
              <span class="a11y-row__label">{{ $t('a11y.fontSize') }}</span>
              <input type="range" min="1" max="1.6" step="0.1" v-model.number="settings.fontScale" class="a11y-range" />
              <span class="a11y-row__val">{{ Math.round(settings.fontScale * 100) }}%</span>
            </label>

            <!-- Zoom -->
            <label class="a11y-row">
              <span class="a11y-row__label">{{ $t('a11y.zoom') }}</span>
              <input type="range" min="1" max="1.6" step="0.1" v-model.number="settings.zoom" class="a11y-range" />
              <span class="a11y-row__val">{{ Math.round(settings.zoom * 100) }}%</span>
            </label>

            <hr class="a11y-sep" />

            <!-- Screen reader -->
            <div class="a11y-block">
              <span class="a11y-block__title">{{ $t('a11y.screenReader') }}</span>
              <label class="a11y-check">
                <input type="checkbox" v-model="settings.reader" />
                <span>{{ $t('a11y.toggle') }}</span>
              </label>
              <div class="a11y-voices" :class="{ 'a11y-voices--off': !settings.reader }">
                <label class="a11y-radio">
                  <input type="radio" value="male" v-model="settings.voice" :disabled="!settings.reader" />
                  <span>{{ $t('a11y.maleVoice') }}</span>
                </label>
                <label class="a11y-radio">
                  <input type="radio" value="female" v-model="settings.voice" :disabled="!settings.reader" />
                  <span>{{ $t('a11y.femaleVoice') }}</span>
                </label>
              </div>
              <p class="a11y-hint">{{ $t('a11y.readerHint') }}</p>
            </div>

            <hr class="a11y-sep" />

            <button type="button" class="a11y-btn" :class="{ 'a11y-btn--active': settings.reading }" @click="settings.reading = !settings.reading">
              {{ $t('a11y.readingMode') }}
            </button>

            <button type="button" class="a11y-reset" @click="reset">
              {{ $t('a11y.reset') }}
            </button>
          </div>
        </aside>
      </div>
    </transition>
  </teleport>
</template>

<script>
import { ui, settings, resetSettings } from './state'

export default {
  name: 'AccessibilityPanel',
  data() {
    return {
      ui,
      settings,
      modes: [
        { id: 'default', labelKey: 'a11y.modeDefault', icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="5"/><path d="M12 1v3M12 20v3M4.2 4.2l2.1 2.1M17.7 17.7l2.1 2.1M1 12h3M20 12h3M4.2 19.8l2.1-2.1M17.7 6.3l2.1-2.1"/></svg>' },
        { id: 'invert', labelKey: 'a11y.modeInvert', icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="9"/><path d="M12 3a9 9 0 0 1 0 18z" fill="currentColor"/></svg>' },
        { id: 'contrast', labelKey: 'a11y.modeContrast', icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="9"/><path d="M12 3v18" /></svg>' },
        { id: 'gray', labelKey: 'a11y.modeGray', icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="M21 15l-5-5L5 21"/></svg>' }
      ]
    }
  },
  methods: {
    reset() {
      resetSettings()
    }
  }
}
</script>

<style lang="scss">
.a11y-overlay {
  position: fixed; inset: 0; z-index: 5000;
  background: rgba(8, 15, 35, 0.4);
  display: flex; justify-content: flex-end;
}
.a11y-panel {
  width: 430px; max-width: 92vw; height: 100%; overflow-y: auto;
  background: #fff; box-shadow: -12px 0 40px rgba(8, 15, 35, 0.25);
  display: flex; flex-direction: column;
  font-family: system-ui, -apple-system, "Segoe UI", Roboto, Arial, sans-serif;
  color: #1f2a3d;
}
.a11y-panel__head {
  display: flex; align-items: center; gap: 14px;
  padding: 22px 24px 18px;
}
.a11y-x {
  border: 0; background: transparent; font-size: 30px; line-height: 1;
  color: #475569; cursor: pointer; padding: 0 4px;
}
.a11y-x:hover { color: #0f172a; }
.a11y-panel__title { margin: 0; font-size: 21px; font-weight: 700; flex: 1; text-align: center; padding-right: 30px; }
.a11y-panel__body { padding: 6px 24px 32px; display: flex; flex-direction: column; gap: 20px; }

.a11y-modes { display: flex; gap: 14px; }
.a11y-mode {
  width: 56px; height: 56px; border-radius: 12px; border: 0; cursor: pointer;
  background: #8b94a3; color: #fff; display: grid; place-items: center;
  transition: background .15s, transform .05s;
}
.a11y-mode svg { width: 26px; height: 26px; }
.a11y-mode:hover { background: #6b7585; }
.a11y-mode:active { transform: scale(.96); }
.a11y-mode--active { background: #15448f; }

.a11y-row { display: grid; grid-template-columns: 1fr; gap: 10px; }
.a11y-row__label { font-size: 16px; font-weight: 600; }
.a11y-row__val {
  justify-self: start; background: #6b7280; color: #fff; font-size: 13px;
  font-weight: 700; padding: 3px 9px; border-radius: 6px;
}
.a11y-range { width: 100%; accent-color: #15448f; height: 6px; }

.a11y-sep { border: 0; border-top: 1px solid #e6e9f0; margin: 4px 0; }

.a11y-block { display: flex; flex-direction: column; gap: 12px; }
.a11y-block__title { font-size: 16px; font-weight: 600; }
.a11y-check, .a11y-radio { display: inline-flex; align-items: center; gap: 9px; font-size: 15px; cursor: pointer; }
.a11y-check input { width: 18px; height: 18px; accent-color: #15448f; }
.a11y-radio input { width: 16px; height: 16px; accent-color: #15448f; }
.a11y-voices { display: flex; gap: 26px; }
.a11y-voices--off { opacity: .5; }
.a11y-hint { margin: 0; font-size: 12.5px; color: #8a93a6; }

.a11y-btn {
  width: 100%; padding: 14px; border: 1px solid #d8dde7; border-radius: 10px;
  background: #fff; font-size: 16px; font-weight: 700; color: #1f2a3d; cursor: pointer;
  transition: background .15s, color .15s, border-color .15s;
}
.a11y-btn:hover { border-color: #15448f; color: #15448f; }
.a11y-btn--active { background: #15448f; color: #fff; border-color: #15448f; }

.a11y-reset {
  width: 100%; padding: 14px; border: 0; border-radius: 10px;
  background: #eef1f6; color: #6b7280; font-size: 15px; font-weight: 600; cursor: pointer;
}
.a11y-reset:hover { background: #e3e7ef; color: #1f2a3d; }

/* slide-in */
.a11y-enter-active, .a11y-leave-active { transition: opacity .25s ease; }
.a11y-enter-active .a11y-panel, .a11y-leave-active .a11y-panel { transition: transform .28s ease; }
.a11y-enter-from, .a11y-leave-to { opacity: 0; }
.a11y-enter-from .a11y-panel, .a11y-leave-to .a11y-panel { transform: translateX(100%); }

@media (prefers-reduced-motion: reduce) {
  .a11y-enter-active, .a11y-leave-active,
  .a11y-enter-active .a11y-panel, .a11y-leave-active .a11y-panel { transition: none; }
}
</style>
